export interface User {
  uid: string;
  email: string;
  displayName: string | null;
  createdAt: Date;
  isReviewer?: boolean;
  isAdmin?: boolean;
  isOwner?: boolean;
}

export type ProjectDomain = 'Physics' | 'Math' | 'Chemistry' | 'Biology' | 'Computer Science';

export interface DomainOption {
  label: string;
  domain: ProjectDomain;
  verifier: 'python' | 'lean';
}

export const PROJECT_DOMAIN_OPTIONS: DomainOption[] = [
  { label: 'Physics', domain: 'Physics', verifier: 'python' },
  { label: 'Math (Lean verifier)', domain: 'Math', verifier: 'lean' },
  { label: 'Math (Python verifier)', domain: 'Math', verifier: 'python' },
  { label: 'Chemistry', domain: 'Chemistry', verifier: 'python' },
  { label: 'Biology', domain: 'Biology', verifier: 'python' },
  { label: 'Computer Science', domain: 'Computer Science', verifier: 'python' },
];

export const PROJECT_DOMAINS: ProjectDomain[] = [
  'Physics',
  'Math',
  'Chemistry',
  'Biology',
  'Computer Science',
];

export interface Project {
  id: string;
  ownerId: string;
  title: string;
  folder: string | null;
  reviewEnabled?: boolean;
  tags?: string[];
  createdAt: Date;
  updatedAt: Date;
}

export type ReviewStatus =
  | 'waiting_for_review'
  | 'in_review'
  | 'approved'
  | 'needs_work'
  | 'complete'
  | 'removed';

export type ReviewNoteSource = 'submission' | 'reviewer';

export interface ReviewNote {
  authorName: string;
  createdAt: Date;
  source: ReviewNoteSource;
  status: ReviewStatus;
  text: string;
}

export interface ReviewRequest {
  id: string;
  projectId: string;
  ownerId: string;
  projectTitle: string;
  status: ReviewStatus;
  submitterId: string;
  submitterName: string;
  submitterNotes: string;
  adminNotes?: string;
  tags?: string[];
  createdAt: Date;
  updatedAt: Date;
  reviewedAt?: Date | null;
  reviewedBy?: string | null;
  reviewedByName?: string | null;
  hasPreviousApproval?: boolean;
  previousReviewCount?: number;
  noteEntries?: ReviewNote[];
}

export type ShareRole = 'read' | 'comment' | 'edit';

export interface SharedProject {
  id: string;
  projectId: string;
  ownerId: string;
  title: string;
  role: ShareRole;
  addedAt: Date;
  updatedAt: Date;
}

export interface ProjectFile {
  id: string;
  name: string;
  content: string;
  type: 'tex' | 'bib' | 'folder' | 'image' | 'other';
  parentId: string | null;
  /** OT version counter (0-based, incremented per committed operation) */
  version?: number;
  /** Whether OT real-time sync is enabled for this file */
  otEnabled?: boolean;
  /** UID of the last user who modified this file via OT */
  lastModifiedBy?: string;
  /** Cloud Storage path when content exceeds the inline Firestore threshold */
  storageContentPath?: string;
  /** Byte size of the stored content (set for storage-backed files) */
  storageSizeBytes?: number;
}

export interface Revision {
  id: string;
  createdAt: Date;
  fileCount: number;
  pdfSize: number;
  label: string | null;
}

export interface RevisionFiles {
  files: Array<{
    name: string;
    content: string;
    type: 'tex' | 'bib' | 'folder' | 'image' | 'other';
    parentId: string | null;
  }>;
  capturedAt: string;
}

export interface CommentSelection {
  startLine: number;
  endLine: number;
  startColumn: number;
  endColumn: number;
  text: string;
}

export interface Comment {
  id: string;
  projectId: string;
  fileId: string;
  authorId: string;
  text: string;
  createdAt: Date;
  selection: CommentSelection;
  anchorBefore: string;
  anchorAfter: string;
  snippet: string;
  resolved?: boolean;
  resolvedAt?: Date | null;
  resolvedBy?: string | null;
}

export interface CommentReply {
  id: string;
  authorId: string;
  text: string;
  createdAt: Date;
}

export * from './ai';
export * from './chat';
export * from './verifier';
